import {
  assertProjectDocument,
  type ResourceRoomProjectDocumentV2
} from "../storage/resourceRoomDocument";
import { ApiError, apiErrorFromResponse } from "./errors";

export interface CommandResponse<T> {
  document: ResourceRoomProjectDocumentV2;
  result: T;
  mutated: boolean;
}

export interface CandidateSessionBlock {
  session_index: number;
  weekday: number;
  time_slot_ids: string[];
}

export interface CandidateExposure {
  student_id: string;
  session_index: number;
  weekday: number;
  time_slot_id: string;
  original_course_id: string | null;
  original_course_name: string | null;
  nature: "PREFERRED" | "GENERAL" | "PROTECTED" | null;
}

export interface SchedulingCandidate {
  signature: string;
  sessions: CandidateSessionBlock[];
  exposures: CandidateExposure[];
  protected_exposure_count: number;
  general_exposure_count: number;
  distinct_day_count: number;
  daily_session_counts: number[];
}

export interface SchedulingCandidatesResponse {
  condition_fingerprint: string;
  candidates: SchedulingCandidate[];
  has_more: boolean;
  missing_session_count: number;
  conflict_categories: string[];
}

export interface TimetableLessonView {
  plan_id: string;
  session_id: string;
  weekday: number;
  time_slot_ids: string[];
  intervention_course_name: string;
  lesson_type: "INDIVIDUAL" | "GROUP";
  student_ids: string[];
  student_names: string[];
  grade_labels: string[];
  class_names: string[];
  main_teacher_id: string;
  main_teacher_name: string;
  assistant_teacher_ids: string[];
  assistant_teacher_names: string[];
  room_id: string;
  room_name: string;
  teacher_role: "MAIN" | "ASSISTANT" | null;
  original_course_name: string | null;
  original_course_names: string[];
}

export interface TimetableViewResponse {
  view: "ROOM" | "TEACHER" | "STUDENT" | "CLASS";
  week_parity: "ODD" | "EVEN";
  time_slots: ResourceRoomProjectDocumentV2["entities"]["time_slots"];
  schedules: Array<{
    object_id: string;
    object_label: string;
    lessons: TimetableLessonView[];
  }>;
}

export async function executeDocumentCommand<T>(
  document: ResourceRoomProjectDocumentV2,
  commandType: string,
  payload: Record<string, unknown>,
  signal?: AbortSignal
): Promise<CommandResponse<T>> {
  const response = await statelessFetch("/api/v2/project/commands", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({
      document,
      expected_document_revision: document.document_revision,
      command: { type: commandType, payload }
    }),
    signal
  });
  if (!response.ok) throw await apiErrorFromResponse(response);
  const result = (await response.json()) as CommandResponse<T>;
  assertProjectDocument(result.document);
  return result;
}

export interface ExportDownload {
  blob: Blob;
  filename: string;
}

export async function exportDocument(
  document: ResourceRoomProjectDocumentV2,
  payload: Record<string, unknown>,
  signal?: AbortSignal
): Promise<ExportDownload> {
  const response = await statelessFetch("/api/v2/exports/xlsx", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ document, ...payload }),
    signal
  });
  if (!response.ok) throw await apiErrorFromResponse(response);
  const disposition = response.headers.get("content-disposition") ?? "";
  const encodedName = disposition.match(/filename\*=UTF-8''([^;]+)/i)?.[1];
  const content = await response.arrayBuffer();
  return {
    blob: new Blob([content], {
      type:
        response.headers.get("content-type") ??
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    }),
    filename: encodedName ? decodeURIComponent(encodedName) : "课表.xlsx"
  };
}

export async function searchCandidates(
  document: ResourceRoomProjectDocumentV2,
  payload: Record<string, unknown>,
  signal?: AbortSignal
): Promise<SchedulingCandidatesResponse> {
  return postDocumentJson<SchedulingCandidatesResponse>(
    "/api/v2/scheduling/candidates",
    document,
    payload,
    signal
  );
}

export async function commitCandidate(
  document: ResourceRoomProjectDocumentV2,
  payload: Record<string, unknown>,
  signal?: AbortSignal
): Promise<CommandResponse<Record<string, unknown>>> {
  const result = await postDocumentJson<CommandResponse<Record<string, unknown>>>(
    "/api/v2/scheduling/commit",
    document,
    payload,
    signal
  );
  assertProjectDocument(result.document);
  return result;
}

export async function scheduleGlobal(
  document: ResourceRoomProjectDocumentV2,
  confirmProtected: boolean,
  signal?: AbortSignal
): Promise<CommandResponse<Record<string, unknown>>> {
  const result = await postDocumentJson<CommandResponse<Record<string, unknown>>>(
    "/api/v2/scheduling/global/schedule",
    document,
    { confirm_protected: confirmProtected },
    signal
  );
  assertProjectDocument(result.document);
  return result;
}

export async function viewTimetable(
  document: ResourceRoomProjectDocumentV2,
  payload: Record<string, unknown>,
  signal?: AbortSignal
): Promise<TimetableViewResponse> {
  return postDocumentJson<TimetableViewResponse>(
    "/api/v2/timetables/view",
    document,
    payload,
    signal,
    false
  );
}

async function postDocumentJson<T>(
  path: string,
  document: ResourceRoomProjectDocumentV2,
  payload: Record<string, unknown>,
  signal?: AbortSignal,
  includeExpectedRevision = true
): Promise<T> {
  const response = await statelessFetch(path, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({
      document,
      ...(includeExpectedRevision
        ? { expected_document_revision: document.document_revision }
        : {}),
      ...payload
    }),
    signal
  });
  if (!response.ok) throw await apiErrorFromResponse(response);
  return (await response.json()) as T;
}

export async function statelessRequest<T>(path: string, signal?: AbortSignal): Promise<T> {
  const response = await statelessFetch(`/api/v2${path}`, { signal });
  if (!response.ok) throw await apiErrorFromResponse(response);
  return (await response.json()) as T;
}

async function statelessFetch(
  input: RequestInfo | URL,
  init?: RequestInit
): Promise<Response> {
  try {
    return await fetch(input, init);
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError") throw error;
    throw new ApiError({
      error: {
        code: "SERVICE_UNAVAILABLE",
        message: "计算服务暂时不可用，本地课表未被改动，请稍后重试",
        field_errors: [],
        details: {},
        request_id: "client"
      }
    });
  }
}
